import { Box, Checkbox, FormGroup, Paper, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useTranslation } from 'react-i18next'

import type { PromptTypeKey } from '@/utils/testPromptAnswerMatrix'
import { TestCreateSection } from '../TestCreateSection'

/**
 * Отображение карточки: что ученик видит вместо вопроса.
 *
 * Список отображений задаёт форма, а не компонент: у теста из урока их три, у
 * самостоятельного — столько, сколько разрешает контур ученика. `idPrefix`
 * нужен, чтобы `htmlFor` не столкнулся, если обе формы окажутся на одной
 * странице.
 */
export function TestDisplaySection({
  options,
  selected,
  onToggle,
  idPrefix,
  hint,
}: {
  options: PromptTypeKey[]
  selected: PromptTypeKey[]
  onToggle: (promptType: PromptTypeKey) => void
  idPrefix: string
  /** Пояснение под заголовком: у форм оно разное. */
  hint?: string
}) {
  const { t } = useTranslation()

  return (
    <TestCreateSection
      id="display"
      icon={<VisibilityIcon fontSize="small" />}
      title={t('tests.createForm.sectionDisplay')}
    >
      {hint ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
          {hint}
        </Typography>
      ) : null}
      <FormGroup>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          {options.map((id) => {
            const checked = selected.includes(id)
            const inputId = `${idPrefix}-prompt-${id}`
            return (
              <Paper
                key={id}
                variant="outlined"
                sx={(theme) => ({
                  overflow: 'hidden',
                  borderColor: checked ? theme.palette.primary.main : theme.palette.divider,
                  bgcolor: checked
                    ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.22)
                    : theme.palette.background.paper,
                  transition: theme.transitions.create(['background-color', 'border-color'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                })}
              >
                <Box
                  component="label"
                  htmlFor={inputId}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    p: 1.25,
                    m: 0,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <Checkbox
                    id={inputId}
                    checked={checked}
                    onChange={() => onToggle(id)}
                    color="primary"
                    sx={{
                      p: 0,
                      mt: 0.125,
                      color: (theme) => (checked ? theme.palette.primary.main : theme.palette.action.active),
                      '&.Mui-checked': {
                        color: (theme) => theme.palette.primary.main,
                      },
                    }}
                  />
                  <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, pt: 0.125 }}>
                    <Typography
                      variant="body2"
                      sx={(theme) => ({
                        fontWeight: 600,
                        color: checked
                          ? theme.palette.mode === 'light'
                            ? theme.palette.primary.dark
                            : theme.palette.primary.light
                          : theme.palette.text.primary,
                      })}
                    >
                      {t(`tests.run.promptTypes.${id}`)}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="span"
                      sx={(theme) => ({
                        display: 'block',
                        lineHeight: 1.35,
                        color: checked
                          ? theme.palette.mode === 'light'
                            ? alpha(theme.palette.primary.dark, 0.92)
                            : alpha(theme.palette.primary.light, 0.9)
                          : theme.palette.text.secondary,
                      })}
                    >
                      {t(`tests.createForm.promptTypeDescriptions.${id}`, { defaultValue: '' })}
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            )
          })}
        </Box>
      </FormGroup>
    </TestCreateSection>
  )
}
